import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { FaPlus, FaTrash, FaSave, FaTimes, FaMinus } from 'react-icons/fa';
import { successToast, errorToast } from "../../Utils/toast";
import { useCreateBulkGaonsMutation } from "../../redux/services/gaonService/gaon.Services";
import { useGetWaadDropdownQuery } from "../../redux/services/waadService/waad.Services";

function AddBulkGaon() {
    const navigate = useNavigate();
    const [createBulkGaons, { isLoading }] = useCreateBulkGaonsMutation();
    const { data: waadData, isLoading: waadLoading } = useGetWaadDropdownQuery();

    // Form state
    const [formData, setFormData] = useState({
        waad_id: "",
        pincode: "",
        status: "active",
    });

    // Gaon list with name and population
    const [gaonList, setGaonList] = useState([
        { name: "", population: "" }
    ]);

    // Handle form change
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    // Handle gaon input change
    const handleGaonChange = (index, field, value) => {
        const updatedList = [...gaonList];
        updatedList[index][field] = value;
        setGaonList(updatedList);
    };

    // Add new gaon row
    const addGaonRow = () => {
        setGaonList([...gaonList, { name: "", population: "" }]);
    };

    // Remove gaon row
    const removeGaonRow = (index) => {
        if (gaonList.length === 1) {
            errorToast("At least one gaon is required");
            return;
        }
        const updatedList = gaonList.filter((_, i) => i !== index);
        setGaonList(updatedList);
    };

    // Clear all gaons
    const clearAllGaons = () => {
        setGaonList([{ name: "", population: "" }]);
    };

    // Submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.waad_id) {
            errorToast("Please select a waad");
            return;
        }

        // Filter out empty rows automatically so empty rows don't block saving
        const validGaons = gaonList.filter(gaon => gaon.name.trim());
        
        if (validGaons.length === 0) {
            errorToast("Please enter at least one gaon name");
            return;
        }

        try {
            // Prepare payload
            const gaonNames = validGaons.map(g => g.name.trim()).join(", ");
            
            const payload = {
                waad_id: parseInt(formData.waad_id),
                gaon_names: gaonNames,
                pincode: formData.pincode || null,
                population: validGaons[0]?.population || 0, // First gaon population as default
                status: formData.status === "active" ? 1 : 0,
            };

            const response = await createBulkGaons(payload).unwrap();
            
            const message = response.data?.skipped 
                ? `${response.data.inserted} gaons created, ${response.data.skipped.count} skipped`
                : response.message || "Gaons created successfully";
            
            successToast(message);
            
            // Reset form
            setFormData({
                waad_id: "",
                pincode: "",
                status: "active",
            });
            setGaonList([{ name: "", population: "" }]);
            
            setTimeout(() => {
                navigate("/gaon");
            }, 1500);
            
        } catch (error) {
            console.log(error);
            errorToast(error?.data?.message || "Failed to create gaons");
        }
    };

    return (
        <div>
            {/* HEADER */}
            <div className="page-header-breadcrumb all_breadcrumb">
                <div className="ms-md-1 mb-1 mb-md-0 ms-0 page_heading_breadcrumb">
                    <nav>
                        <h4 className="fw-semibold">Add Multiple Gaons</h4>
                        <ol className="breadcrumb mb-0">
                            <li className="breadcrumb-item active" aria-current="page">
                                <Link to="/">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <Link to="/gaon">Gaon List</Link>
                            </li>
                            <li className="breadcrumb-item">Add Multiple Gaons</li>
                        </ol>
                    </nav>
                </div>
                <div className="page-title fw-semibold fs-18 mb-0">
                    <button
                        className="btn btn-primary me-2"
                        onClick={() => navigate("/gaon")}
                    >
                        <span>
                            <i className="fe fe-list me-1"></i>
                        </span>
                        Gaon List
                    </button>
                </div>
            </div>

            {/* FORM */}
            <div className="card">
                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        <div className="row">
                            {/* WAAD SELECT */}
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="fw-semibold">
                                        Select Waad <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        name="waad_id"
                                        className="form-control"
                                        value={formData.waad_id}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">Select Waad</option>
                                        {waadData?.data?.map((waad) => (
                                            <option key={waad.id} value={waad.id}>
                                                {waad.waad_name} {waad.waad_code ? `(${waad.waad_code})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                    {waadLoading && (
                                        <small className="text-muted">Loading waads...</small>
                                    )}
                                </div>
                            </div>

                            {/* PINCODE */}
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="fw-semibold">Pincode</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter Pincode"
                                        name="pincode"
                                        value={formData.pincode}
                                        onChange={handleChange}
                                        maxLength="10"
                                    />
                                </div>
                            </div>

                            {/* STATUS */}
                            <div className="col-md-6 mt-3">
                                <div className="form-group">
                                    <label className="fw-semibold">Status</label>
                                    <select
                                        name="status"
                                        className="form-control"
                                        value={formData.status}
                                        onChange={handleChange}
                                    >
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* DIVIDER */}
                        <hr className="my-4" />

                        {/* GAON LIST HEADER */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="fw-semibold mb-0">
                                <FaPlus className="me-2 text-primary" />
                                Gaon List ({gaonList.filter(g => g.name.trim()).length} added)
                            </h5>
                            <div>
                                <button
                                    type="button"
                                    className="btn btn-danger btn-sm me-2"
                                    onClick={clearAllGaons}
                                >
                                    <FaTrash className="me-1" />
                                    Clear All
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={addGaonRow}
                                >
                                    <FaPlus className="me-1" />
                                    Add Row
                                </button>
                            </div>
                        </div>

                        {/* GAON INPUT ROWS */}
                        <div className="table-responsive">
                            <table className="table table-bordered table-hover">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: "40px" }}>#</th>
                                        <th>Gaon Name <span className="text-danger">*</span></th>
                                        <th style={{ width: "150px" }}>Population</th>
                                        <th style={{ width: "60px" }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {gaonList.map((gaon, index) => (
                                        <tr key={index}>
                                            <td className="text-center">{index + 1}</td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter gaon name"
                                                    value={gaon.name}
                                                    onChange={(e) => handleGaonChange(index, 'name', e.target.value)}
                                                    autoFocus={index === gaonList.length - 1}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    placeholder="Population"
                                                    value={gaon.population}
                                                    onChange={(e) => handleGaonChange(index, 'population', e.target.value)}
                                                    min="0"
                                                />
                                            </td>
                                            <td className="text-center">
                                                {gaonList.length > 1 && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => removeGaonRow(index)}
                                                    >
                                                        <FaTimes />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="table-light">
                                    <tr>
                                        <td colSpan="4">
                                            <button
                                                type="button"
                                                className="btn btn-outline-primary w-100"
                                                onClick={addGaonRow}
                                            >
                                                <FaPlus className="me-1" />
                                                Add Another Gaon
                                            </button>
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        {/* SUMMARY */}
                        <div className="alert alert-info mt-3">
                            <div className="row">
                                <div className="col-md-4">
                                    <strong>Total Gaons:</strong> {gaonList.filter(g => g.name.trim()).length}
                                </div>
                                <div className="col-md-4">
                                    <strong>Selected Waad:</strong> {
                                        waadData?.data?.find(w => w.id == formData.waad_id)?.waad_name || 'Not selected'
                                    }
                                </div>
                                <div className="col-md-4">
                                    <strong>Pincode:</strong> {formData.pincode || 'Not set'}
                                </div>
                            </div>
                        </div>

                        {/* SUBMIT BUTTONS */}
                        <div className="d-flex gap-2 mt-4">
                            <button
                                type="submit"
                                className="btn btn-success"
                                disabled={isLoading || gaonList.filter(g => g.name.trim()).length === 0}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <FaSave className="me-1" />
                                        Save All ({gaonList.filter(g => g.name.trim()).length} Gaons)
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => navigate("/gaon")}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* QUICK TIPS */}
            <div className="card mt-3">
                <div className="card-body">
                    <h6 className="fw-semibold">
                        <i className="fe fe-info me-2 text-info"></i>
                        Quick Tips
                    </h6>
                    <ul className="mb-0 small">
                        <li>Click <strong>"Add Row"</strong> or <strong>"Add Another Gaon"</strong> to add more gaons</li>
                        <li>Press <strong>Enter</strong> to quickly move to next input</li>
                        <li>Population is optional - leave blank for 0</li>
                        <li>Pincode will be applied to all gaons</li>
                        <li>All gaons will be created in the selected waad</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default AddBulkGaon;